import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { IActionHandler, MessageDisplayType } from "@preveil-api";
import {
    Account, useAppSelector, useAppDispatch, TreeItem, prettyBreadcrumbs, Attachment, DriveConstants, TreeItemType, useFetchDirectory,
    useFetchLink, buildDriveTreeFromRoot, MessageAnchors, GlobalErrorMessages, Message, MessageHandlerDisplayType, DirectoryEntity
} from "src/common";
import { Loading, Tree } from "src/components";
import { uiActions, driveActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
    current_account: Account;
    current_attachment?: Attachment;
    handleHideModal: () => void;
}

function AttachmentModalComponent(props: AllProps) {
    const { current_account, current_attachment, handleHideModal } = props;
    const root_info = useAppSelector((state: RootState) => state.drive.root_info);
    const [show, setShow] = useState<boolean>(!!current_attachment);
    const [root_tree, setRootTree] = useState<TreeItem>();
    const [tree_item, setTreeItem] = useState<TreeItem>();
    const [destination, setDestination] = useState<TreeItem>();
    const [fetching_children, setFetchingChildren] = useState<boolean>(false);
    const [active, setActive] = useState<string | undefined>();
    const { directory, error: dir_error, fetchDirectory, destroyFetchDirectory } = useFetchDirectory(current_account);
    const { directory_id, directory_entity, error: link_error, fetchLink, resetLink } = useFetchLink(current_account);
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(uiActions.setUploadProgressHidden(false));
        return () => {
            dispatch(uiActions.setUploadProgressHidden(true));
        };
    }, []);

    // Description: On load of Thread View - build tree for current directory as well as root tree
    useEffect(() => {
        !!root_info && fetchDirectory(root_info.collection_id, root_info.id);
    }, [root_info]);

    // Description: Build tree structure on fetch Directory success or error
    useEffect(() => {
        dir_error ? handleFetchDirectoryError() :
            (!!directory && handleFetchDirectorySuccess(directory));
    }, [directory, dir_error]);

    // Description: Build tree structure after fetch LINK_ENTITY success or error
    useEffect(() => {
        if (!!link_error) {
            handleFetchDirectoryError();
        } else if (!!directory_id) {
            !!tree_item?.linked_collection_id ?
                fetchDirectory(tree_item.linked_collection_id, directory_id) :
                handleFetchDirectoryError();
        } else if (!!directory_entity) {
            handleFetchDirectorySuccess(directory_entity);
        }
    }, [directory_id, directory_entity, link_error]);

    // Description: Get attachment details from current_thread messages then open modal else throw error
    useEffect(() => {
        setShow(!!current_attachment);
    }, [current_attachment]);

    const AttachmentModalRequests = {
        // Description: Sets the active item as well as the destination (on click of a node)
        handleTreeNodeClick: (item: TreeItem) => {
            if (!!item.type) {
                !!item.id ? setActive(item.id) : setActive(undefined);
                setDestination(item);
            }
        },
        // Description: On click of the chevron, fetches the nodes children through fetch directory
        handleFetchTreeNodeChildren: (item: TreeItem) => {
            const _collection_id = item?.collection_id;
            if (!!item.id && !!_collection_id) {
                setTreeItem(item);
                item.children_loading = true;
                setFetchingChildren(true);
                !!item.linked_collection_id ? fetchLink(_collection_id, item.id) :
                    fetchDirectory(_collection_id, item.id);
            } else {
                handleFetchDirectoryError();
            }
        },
        handlePageErrorMessage: (params: { message: string; stack?: any, display_type?: MessageDisplayType }) => {
            dispatch(
                uiActions.handleRequestErrors(
                    new Message(params.message, params.display_type || MessageHandlerDisplayType.logger),
                    params.stack,
                ),
            );
        },
    };

    //  Description: Handle all actions from Children forms 
    function handlePageActions(actionObj: IActionHandler) {
        const callback = `handle${actionObj.actionType}`;
        if ((AttachmentModalRequests as any)[callback] instanceof Function) {
            (AttachmentModalRequests as any)[callback](actionObj.params);
        } else {
            const message = GlobalErrorMessages.no_handler_found.replace(
                MessageAnchors.actionType,
                actionObj.actionType,
            );
            AttachmentModalRequests.handlePageErrorMessage({ message, stack: actionObj });
        }
    }

    // Description: Handle callback from FetchDirectory
    function handleFetchDirectorySuccess(_directory: DirectoryEntity) {
        dispatch(driveActions.setCurrentDirectorySuccess(_directory));
        // If the custom root tree is not populated yet, it populates it here
        if (!root_tree) {
            const children = buildDriveTreeFromRoot(_directory, DriveConstants.ROOT_DIRECTORY);
            const root = {
                id: _directory.id,
                collection_id: _directory.collection_id,
                name: DriveConstants.ROOT_DIRECTORY,
                type: TreeItemType.folder,
                path: DriveConstants.ROOT_DIRECTORY,
                children: !!children && children.length > 0 ? children : undefined,
                expanded: true
            };
            setRootTree(root);
        } else {
            // NOTE: sets the tree_items children when a node is opened (it fetches the children of the node)
            if (!!tree_item) {
                const children = buildDriveTreeFromRoot(_directory, tree_item.path);
                if (!!children && children.length > 0) {
                    tree_item.children = children;
                }
                tree_item.children_loading = false;
            }
            setFetchingChildren(false);
        }
        destroyFetchDirectory();
        resetLink();
    }

    // Description: Handle FetchDirectory Errors
    function handleFetchDirectoryError() {
        destroyFetchDirectory();
        resetLink();
        if (!!tree_item) {
            tree_item.children_loading = false;
        }
        setFetchingChildren(false);
    }

    // Description: Handle Download to destination
    function handleInitDownload() {
        const collection_id = destination?.collection_id;
        const id = destination?.id;
        if (!!collection_id && !!id && !!current_attachment && !!current_attachment.blob) {
            // NOTE: Convert Blob to File
            const download_file = new File([current_attachment.blob], current_attachment.filename, {
                type: current_attachment.blob.type,
                lastModified: new Date().getTime() // Optional: Set last modified date
            });
            const upload = {
                collection_id,
                node_id: id,
                linked_collection_id: destination.linked_collection_id,
                files: [download_file],
                destination: { id }
            };
            dispatch(driveActions.setFileUploadRequest(upload));
            resetModal();
        } else {
            AttachmentModalRequests.handlePageErrorMessage({
                message: "Unable to download because destination or Blob are missing",
                stack: { current_attachment, destination },
                display_type: MessageHandlerDisplayType.toastr
            });
        }
    }

    // Description: Reset current state to start fresh on open
    function resetModal() {
        setActive(undefined);
        setDestination(undefined);
        setFetchingChildren(false);
        handleHideModal();
    }

    return <Modal
        size="lg"
        dialogClassName="move-modal"
        show={show}
        onHide={resetModal}
        aria-labelledby="Download Attachment Modal" >
        <Modal.Header className="align-items-start" closeButton>
            <p>
                Download File: <b>{current_attachment?.filename}</b>&nbsp;
                {
                    !!destination?.name &&
                    <>
                        to:&nbsp;
                        <span
                            className="text-link"
                            dangerouslySetInnerHTML={{ __html: prettyBreadcrumbs(destination.path) }}
                        />
                    </>
                }
            </p>
        </Modal.Header>
        <Modal.Body>
            <div className="move-folders ps-0">
                {
                    !!root_tree ?
                        <Tree
                            className="move-tree"
                            data={[root_tree]}
                            handleAction={handlePageActions}
                            readonly
                            active={active}
                            fetching_children={fetching_children}
                            drive={true}
                        /> : <Loading className="in-place" />
                }
            </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
            <Button onClick={handleInitDownload} disabled={!destination} >
                Download
            </Button>
            <Button variant="no-outline-primary" onClick={resetModal}>
                Cancel
            </Button>
        </Modal.Footer>
    </Modal>;
}

export default React.memo(AttachmentModalComponent);
